import { Button, GlobalError, H1, Hr } from '@dnb/eufemia';
import { launch as LaunchIcon } from '@dnb/eufemia/icons';
import type { ApiFromCommonRepoDto, VersionFromCommonRepoDto } from '@portals/shared/portal/ApiFromCommonRepoDto';
import { TrackRoute } from '@portals/shared-frontend/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import HeroPage from '@/components/HeroPage';
import HeroTopContent from '@/components/HeroPage/HeroTopContent';
import ReferenceFromCommonRepo from '@/pages/explorer/apis/ReferenceFromCommonRepo';

import HeaderControls from './components/HeaderControls';

import './styles/override.css';

import * as styles from './styles/Api.styles';

const GITLAB_BASE_URL = 'https://gitlab.tech.dnb.no/dnb/api-governance/api-contracts/-/blob/main';

const CommonRepoApiDocumentation = () => {
  const { data: apis, isValidating: apisLoading } = useSWR<ApiFromCommonRepoDto[]>('/common-repo/apis');
  const { tab: tabParam = 'reference', apiSlug, environment: environmentParam, version: versionParam } = useParams();
  const navigate = useNavigate();
  const [environment, setEnvironment] = useState('production');
  const [version, setVersion] = useState<VersionFromCommonRepoDto>();

  const api = useMemo(() => {
    return apis?.find((api) => api.slug === apiSlug);
  }, [apiSlug, apis]);

  const environmentDropdown = useMemo(() => {
    return [...new Set(api?.versions.map((version) => version.environment))].map((environment) => ({
      content: environment,
      key: environment,
    }));
  }, [api?.versions]);

  const releaseDropdown = useMemo(() => {
    return api?.versions
      .filter((version) => version.environment === environment)
      .map((version) => ({
        content: version.version || '',
        key: version.version || '',
      }));
  }, [api?.versions, environment]);

  const doNavigate = useCallback(
    ({ env = '', release = '', tab = '', replace = true }) => {
      let url = `/api-contracts/${apiSlug}`;
      url += env || environmentParam ? '/' + (env || environmentParam) : '';
      url += release || versionParam ? '/' + (release || versionParam) : '';
      url += tab || tabParam ? '/' + (tab || tabParam) : '';
      url += globalThis.location.hash ?? globalThis.location.hash;
      navigate(url, { replace: replace });
    },
    [versionParam, environmentParam, tabParam, apiSlug, navigate],
  );

  /* 2. SET ALL ENVIRONMENTS */
  useEffect(() => {
    if (api) {
      const env =
        !environmentParam || environmentParam === '@default'
          ? (environmentDropdown.at(-1)?.key ?? '')
          : environmentParam;
      setEnvironment(env);
      doNavigate({ env });
    }
  }, [environmentParam, apiSlug, doNavigate, api, environmentDropdown]);

  /* 3. SET ALL RELEASES */
  useEffect(() => {
    if (api && environment) {
      const ver = !versionParam || versionParam === '@latest' ? null : versionParam;

      const versionPerEnv = api.versions.filter((version) => version.environment === environment);

      const latestVersion = versionPerEnv.find((version) =>
        ver ? version.version === versionParam : version.isLatest,
      );
      const _version = latestVersion || versionPerEnv[0];
      setVersion(_version);
      doNavigate({
        release: _version?.version ?? undefined,
      });
    }
  }, [api, doNavigate, environment, version?.version, versionParam]);

  return (
    <div className="dark-mode">
      <HeroPage
        banner="documentation"
        heroLeftComponent={
          <styles.HeroHeaderContent>
            <H1 size="x-large">{api?.title}</H1>
          </styles.HeroHeaderContent>
        }
        noTopMargin
        skeleton={apisLoading}
        title={api?.title}
      >
        <HeroTopContent noBottomPadding>
          <div className="flex justify-between items-end">
            <HeaderControls
              canSubscribe={false}
              doNavigate={doNavigate}
              environments={environmentDropdown}
              releases={releaseDropdown}
              selected={apiSlug ?? null}
              selectedEnvironment={environment}
              selectedVersion={version?.version ?? null}
            />
            {version && (
              <Button
                href={`${GITLAB_BASE_URL}/${version?.apiLink}`}
                icon={<LaunchIcon />}
                rel="noreferrer"
                target="_blank"
                variant="secondary"
              >
                Open in gitlab
              </Button>
            )}
          </div>
          <Hr breakout top="small" />
        </HeroTopContent>
        {api && <TrackRoute name={api.title} path={`/api-contracts/${api.slug}/@default/@latest`} type="api-ccr" />}

        {version?.apiLink ? (
          <ReferenceFromCommonRepo apiLink={version?.apiLink} />
        ) : (
          <GlobalError title="No reference present" />
        )}
      </HeroPage>
    </div>
  );
};

export default CommonRepoApiDocumentation;
